import { computed, ref, type Ref } from "vue";
import api from "@/api";
import constants from "@/api/constants";
import timezones from "@/helpers/timezones";
import { getDateTokens } from "@/helpers/dateFormat";
import { calculateTimeout, getUserDisplayName } from "@/helpers/utilities";
import { useToast } from "vue-toastification";
import { useFileUploadFlow } from "@/composables/useFileUploadFlow";
import type { EntityType } from "@/types/EntityType";
import type { FileUploadCounts } from "@/types/FileUploadCounts";

type DropUploadEntity = { id: string; name?: string | null; projectId?: string | null } | null | undefined;

export interface MediaGridDropUploadOptions {
	/** Authenticated, not view-only, trial not ended — the gate the Documents tab applied. */
	enabled: Ref<boolean>;
	entity: Ref<DropUploadEntity>;
	entityType: Ref<EntityType | null>;
	/** Fed once per uploaded file, exactly like the Add Content flow's `uploadCounts` event. */
	onUploadCounts: (counts: FileUploadCounts) => void;
	/** A project drop creates an uploads session; the page registers it so its media shows up. */
	onConversationCreated?: (conversationId: string) => void | Promise<void>;
}

/**
 * LSP-16659: the drag-and-drop UPLOAD target, moved onto the Session / Project media grid from the
 * retired Documents tab (it was the page's only one). Drops go through the same upload flow as Add
 * Content (useFileUploadFlow → mime routing: image → photo, video → recording, PDF → document).
 *
 * Only an OS file drag counts: Timeline's day view drags a tile between dates inside this same area, and
 * that must never light up the upload overlay or be read as an upload. A composable rather than a
 * wrapper component on purpose: the page's specs shallow-mount it, and a stubbed wrapper would swallow
 * the Timeline slotted inside it.
 */
export function useMediaGridDropUpload(options: MediaGridDropUploadOptions) {
	const { processFiles } = useFileUploadFlow();
	const toast = useToast();
	const dragCounter = ref(0);
	const isDraggingFiles = computed(() => options.enabled.value && dragCounter.value > 0);

	function isFileDrag(event: DragEvent): boolean {
		return Array.from(event.dataTransfer?.types ?? []).includes("Files");
	}

	function onDragenter(event: DragEvent) {
		if (!options.enabled.value || !isFileDrag(event)) return;
		event.preventDefault();
		dragCounter.value += 1;
	}

	function onDragover(event: DragEvent) {
		if (!options.enabled.value || !isFileDrag(event)) return;
		event.preventDefault();
	}

	function onDragleave(event: DragEvent) {
		if (!options.enabled.value || !isFileDrag(event)) return;
		event.preventDefault();
		dragCounter.value = Math.max(0, dragCounter.value - 1);
	}

	async function onDrop(event: DragEvent) {
		if (!isFileDrag(event)) return;
		dragCounter.value = 0;
		if (!options.enabled.value) return;
		event.preventDefault();
		const files = Array.from(event.dataTransfer?.items ?? [])
			.map((item) => item.getAsFile())
			.filter((file): file is File => file !== null);
		if (!files.length) {
			// Silent-failure rule: a drop that carried no readable file would otherwise just do nothing.
			console.warn("useMediaGridDropUpload: a file drop carried no readable files; nothing to upload", {
				itemCount: event.dataTransfer?.items?.length ?? 0
			});
			return;
		}
		await uploadFiles(files);
	}

	async function uploadFiles(files: File[]): Promise<void> {
		const uploadEntity = options.entity.value ? { ...options.entity.value } : null;
		const uploadEntityType = options.entityType.value;
		if (!uploadEntity?.id || !uploadEntityType) {
			console.warn("useMediaGridDropUpload: no entity loaded; ignoring the drop", {
				fileCount: files.length,
				entityType: uploadEntityType
			});
			return;
		}
		const onFileUploaded = (fileType: "image" | "document" | "video") => {
			options.onUploadCounts({
				conversationId: resolvedConversationId ?? undefined,
				projectId: uploadEntityType === "project" ? uploadEntity.id : uploadEntity.projectId,
				images: fileType === "image" ? 1 : 0,
				documents: fileType === "document" ? 1 : 0,
				videos: fileType === "video" ? 1 : 0
			} satisfies FileUploadCounts);
		};
		let resolvedConversationId: string | null = uploadEntityType === "conversation" ? uploadEntity.id : null;
		const getConversationData = async () => {
			if (uploadEntityType === "conversation") {
				return { id: uploadEntity.id, name: uploadEntity.name ?? "", projectId: uploadEntity.projectId };
			}
			// A project upload lands in a new uploads session, exactly as the Documents tab created it.
			const currentUser = await api.getMe();
			const date = timezones.formatDate(
				new Date().toISOString(),
				getDateTokens(currentUser?.organization?.dateFormat).dateShortMonth
			);
			const conversationTitle = currentUser ? `Files from ${getUserDisplayName(currentUser?.user)} on ${date}` : `Files on ${date}`;
			const response = await api.createConversation({
				forcedUserId: currentUser?.user.id,
				name: conversationTitle,
				type: constants.CONVERSATION_TYPE.UPLOADS,
				initiationType: constants.INITIATION_TYPE.ADMIN,
				projectId: uploadEntity.id
			});
			const { id, name } = response.conversation;
			resolvedConversationId = id;
			try {
				await options.onConversationCreated?.(id);
			} catch (error) {
				console.warn("useMediaGridDropUpload: could not register the new uploads session with the page", {
					conversationId: id,
					error
				});
			}
			return { id, name, projectId: uploadEntity.id };
		};
		try {
			await processFiles(files, getConversationData, { onFileUploaded });
		} catch (error) {
			// Silent-failure rule: a rejection here (resolving the uploads session, or the upload flow itself)
			// used to be console-only, so a drop simply never uploaded. Log the ids and tell the user, with the
			// same copy the Add Content flow uses when an upload cannot start.
			console.error("useMediaGridDropUpload: the dropped files could not be uploaded", {
				entityType: uploadEntityType,
				projectId: uploadEntityType === "project" ? uploadEntity.id : null,
				conversationId: resolvedConversationId,
				fileCount: files.length,
				error
			});
			const message = "Sorry, we were unable to upload some files. Please try to upload them again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	return { isDraggingFiles, onDragenter, onDragover, onDragleave, onDrop, uploadFiles };
}
